import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  HiOutlineLockClosed, HiOutlineCalendarDays, HiOutlineMapPin,
  HiOutlineUserGroup, HiOutlineCurrencyDollar, HiOutlineCheckCircle,
  HiOutlineSparkles, HiOutlineEnvelope, HiOutlineHome, HiOutlineClock,
  HiOutlineSun, HiOutlineHeart, HiOutlineArrowRight,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import "./locked.css";
import "./MyRetreatPage.css";
import Picture from "../components/utils/Picture";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" }) : "—";
const fmtMoney = (n) => `$${Number(n||0).toLocaleString("en-US",{minimumFractionDigits:2})}`;


const CATEGORY_COLOR = {
  "Veteran": "#b85c38",
  "First Responder": "#2563eb",
  "Law Enforcement": "#7c3aed",
  "Gold Star Family": "#d97706",
};

export default function MyRetreatPage() {
  const [loading,  setLoading]  = useState(true);
  const [locked,   setLocked]   = useState(false);
  const [cohort,   setCohort]   = useState(null);
  const [payments, setPayments] = useState(null);
  const [history,  setHistory]  = useState([]);
  const [booking,  setBooking]  = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      portalAPI.getCohort().catch((err) => ({ __locked: true })),
      portalAPI.getPayments().catch(() => null),
      portalAPI.getRetreatHistory().catch(() => null),
      portalAPI.booking.mine().catch(() => null),
    ]).then(([cohortRes, paymentsRes, historyRes, bookingRes]) => {
      if (bookingRes?.data) setBooking(bookingRes.data);
      if (cohortRes?.__locked || !cohortRes?.data) {
        setLocked(true);
      } else {
        setCohort(cohortRes.data);
      }
      if (paymentsRes?.data) setPayments(paymentsRes.data);
      if (historyRes?.data) setHistory(historyRes.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <PortalLayout eyebrow="My Retreat" pageTitle="Your Retreat"
        quote="We rise by lifting others." quoteAuthor="Forge Retreat">
        <div className="mrt__loading"><div className="mrt__spinner"/></div>
      </PortalLayout>
    );
  }

  if (locked && history.length === 0) {
    return (
      <PortalLayout eyebrow="My Retreat" pageTitle="Your Retreat"
        quote="We rise by lifting others." quoteAuthor="Forge Retreat">
        <div className="locked__wrap">
          <div className="pc locked__card">
            <div className="locked__icon-ring">
              <HiOutlineLockClosed className="locked__icon"/>
            </div>
            <h2 className="locked__title">Not Yet Available</h2>
            <p className="locked__sub">
              Once you're confirmed for a retreat date, this is where you'll find your cohort —
              the group joining you — along with your retreat details and payment balance, if applicable.
            </p>
            <div className="locked__timeline-note">
              <HiOutlineCalendarDays/>
              <span>Check your Journey page to see where you are in the process.</span>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Between retreats — no current cohort, but there's real history to show
  // (e.g. someone re-queued for a second retreat after completing their first).
  if (locked && history.length > 0) {
    return (
      <PortalLayout eyebrow="My Retreat" pageTitle="Your Retreats"
        quote="We rise by lifting others." quoteAuthor="Forge Retreat">
        <div className="pc mrt__thankyou">
          <HiOutlineClock className="mrt__thankyou-icon" style={{color:"#d97706"}}/>
          <p className="mrt__thankyou-text">
            You're currently between retreats. Check your Journey page to see where you stand
            for your next one — in the meantime, here's your retreat history.
          </p>
        </div>
        <RetreatHistorySection history={history}/>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout eyebrow="My Retreat" pageTitle={cohort.name}
      quote={cohort.isPast ? "We rise by lifting others." : "We rise by lifting others."} quoteAuthor="Forge Retreat">

      {/* Retreat header — framing differs for a past vs. upcoming retreat */}
      <div className={`pc mrt__header ${cohort.isPast ? "mrt__header--past" : ""}`}>
        <div className="mrt__header-icon"><HiOutlineSparkles/></div>
        <div className="mrt__header-body">
          <p className="mrt__header-eyebrow">
            {cohort.isPast
              ? "Retreat Completed"
              : cohort.daysUntil > 0 ? `${cohort.daysUntil} day${cohort.daysUntil!==1?"s":""} to go` : cohort.daysUntil === 0 ? "Today!" : "In progress"}
          </p>
          <p className="mrt__header-dates"><HiOutlineCalendarDays/> {fmt(cohort.start_date)} – {fmt(cohort.end_date)}</p>
          {cohort.location && <p className="mrt__header-location"><HiOutlineMapPin/> {cohort.location}</p>}
        </div>
      </div>

      {/* Pathway booking summary — pathway, room, activity, diet */}
      {!cohort.isPast && booking && (
        <div className="pc mrt__booking">
          <div className="mrt__booking-items">
            {booking.pathway_name && (
              <div className="mrt__booking-item">
                <HiOutlineSparkles />
                <span><small>Pathway</small>{booking.pathway_name}</span>
              </div>
            )}
            {booking.room_type_name && (
              <div className="mrt__booking-item">
                <HiOutlineHome />
                <span><small>Room</small>{booking.room_type_name}</span>
              </div>
            )}
            <div className="mrt__booking-item">
              <HiOutlineSun />
              <span><small>Activity</small>{booking.activity_name || "Not chosen yet"}</span>
            </div>
            <div className="mrt__booking-item">
              <HiOutlineHeart />
              <span><small>Diet</small>{booking.diet_name || "Not specified"}</span>
            </div>
          </div>
          <Link to="/portal/book" className="mrt__booking-link">Manage booking <HiOutlineArrowRight /></Link>
        </div>
      )}

      {cohort.isPast && (
        <div className="pc mrt__thankyou">
          <HiOutlineCheckCircle className="mrt__thankyou-icon"/>
          <p className="mrt__thankyou-text">
            Thank you for being part of Forge. We hope this retreat was meaningful for you —
            we'd love to have you join us again on a future retreat.
          </p>
        </div>
      )}

      {/* Cohort roster */}
      <div className="pc mrt__section">
        <div className="mrt__section-head">
          <HiOutlineUserGroup className="mrt__section-icon"/>
          <p className="p-section-title" style={{margin:0}}>
            {cohort.isPast ? "Who Was There" : cohort.rosterIsCohort ? "Your Ceremony Group" : "Your Cohort"}
          </p>
          {cohort.cohortName && (
            <span className="mrt__cohort-tag">{cohort.cohortName}</span>
          )}
          <span className="mrt__section-count">{cohort.attendeeCount} attend{cohort.isPast ? "ed" : "ing"}</span>
        </div>
        <p className="mrt__section-sub">
          {cohort.isPast
            ? "The people who joined you on this retreat."
            : cohort.rosterIsCohort
              ? "Ceremony happens in small groups — these are the people you'll be sharing that with."
              : "The people joining you on this retreat. We'll share more about each other once you arrive."}
        </p>
        <div className="mrt__roster-grid">
          {cohort.attendees.map(a => (
            <div key={a.id} className={`mrt__roster-card ${a.isYou ? "mrt__roster-card--you" : ""}`}>
              <div className="mrt__roster-avatar" style={{background: CATEGORY_COLOR[a.category] || "#6b6258"}}>
                {a.firstName?.[0]?.toUpperCase()}{a.lastInitial}
              </div>
              <p className="mrt__roster-name">{a.firstName} {a.lastInitial}. {a.isYou && <span className="mrt__roster-you-tag">You</span>}</p>
              <p className="mrt__roster-category">{a.category}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Your room — showcases the actual photos uploaded for this room type */}
      {cohort.yourRoom && (
        <div className="pc mrt__section">
          <div className="mrt__section-head">
            <HiOutlineHome className="mrt__section-icon"/>
            <p className="p-section-title" style={{margin:0}}>{cohort.isPast ? "Your Room Was" : "Your Room"}</p>
          </div>
          <div className="mrt__room-card">
            {cohort.yourRoom.photos && cohort.yourRoom.photos.length > 0 && (
              <div className="mrt__room-photos">
                {cohort.yourRoom.photos.map(p => (
                  <Picture key={p.id} src={p.file_path} alt={cohort.yourRoom.name} sizes="(min-width: 900px) 160px, 33vw" width={400} height={400} />
                ))}
              </div>
            )}
            <p className="mrt__room-name">{cohort.yourRoom.name}</p>
            <p className="mrt__room-rate">{cohort.yourRoom.is_free ? "Free / Scholarship"
              : booking && Number(booking.room_type_id) === Number(cohort.yourRoom.id)
                ? (Number(booking.room_total) > 0 ? `${fmtMoney(booking.room_total)} room upgrade · ${booking.nights} nights` : "Included")
                : fmtMoney(cohort.yourRoom.rate)}</p>
          </div>
        </div>
      )}

      {/* Payment balance — only shown if there's actually a paid room type */}
      {payments && payments.totalOwed > 0 && (
        <div className="pc mrt__section">
          <div className="mrt__section-head">
            <HiOutlineCurrencyDollar className="mrt__section-icon"/>
            <p className="p-section-title" style={{margin:0}}>Your Payment</p>
          </div>

          <div className="mrt__balance-box">
            <div className="mrt__balance-row">
              <span>Total Cost</span>
              <span className="mrt__balance-value">{fmtMoney(payments.totalOwed)}</span>
            </div>
            <div className="mrt__balance-row">
              <span>Paid So Far</span>
              <span className="mrt__balance-value" style={{color:"#16a34a"}}>{fmtMoney(payments.totalPaid)}</span>
            </div>
            <div className="mrt__balance-row mrt__balance-row--main">
              <span>Balance Due</span>
              <span className="mrt__balance-value mrt__balance-value--main" style={{color: payments.balance > 0 ? "#dc2626" : "#16a34a"}}>
                {payments.isPaidInFull ? "Paid in Full ✓" : fmtMoney(payments.balance)}
              </span>
            </div>
          </div>

          {payments.payments.length > 0 && (
            <div className="mrt__payment-history">
              <p className="mrt__payment-history-title">Payment History</p>
              {payments.payments.map(p => (
                <div key={p.id} className="mrt__payment-row">
                  <div>
                    <p className="mrt__payment-amount">{fmtMoney(p.amount)}</p>
                    <p className="mrt__payment-meta">{p.method || "Payment"}{p.note ? ` · ${p.note}` : ""}</p>
                  </div>
                  <p className="mrt__payment-date">{fmt(p.paid_at)}</p>
                </div>
              ))}
            </div>
          )}

          {!payments.isPaidInFull && (
            <div className="mrt__payment-note">
              <HiOutlineEnvelope/>
              <span>To make a payment or ask about your balance, please contact our team — we'll take care of it directly.</span>
            </div>
          )}
          {payments.isPaidInFull && (
            <div className="mrt__payment-note mrt__payment-note--done">
              <HiOutlineCheckCircle/>
              <span>You're all paid up. Thank you!</span>
            </div>
          )}
        </div>
      )}

      {/* Past retreats — every other completed retreat besides the one already
          shown as the hero above, so someone attending their 2nd+ retreat sees
          their full history rather than just the most recent one. */}
      {history.filter(h => h.retreat_block_id !== cohort.id).length > 0 && (
        <RetreatHistorySection history={history.filter(h => h.retreat_block_id !== cohort.id)}/>
      )}
    </PortalLayout>
  );
}

/* ── Past retreats list — full attendance history, oldest details included ── */
function RetreatHistorySection({ history }) {
  return (
    <div className="pc mrt__section">
      <div className="mrt__section-head">
        <HiOutlineCalendarDays className="mrt__section-icon"/>
        <p className="p-section-title" style={{margin:0}}>Past Retreats</p>
        <span className="mrt__section-count">{history.length} attended</span>
      </div>
      <div className="mrt__history-list">
        {history.map(h => (
          <div key={h.id} className="mrt__history-row">
            <div className="mrt__history-icon"><HiOutlineCheckCircle/></div>
            <div className="mrt__history-body">
              <p className="mrt__history-name">{h.block_name}</p>
              <p className="mrt__history-meta">
                <HiOutlineCalendarDays/> {fmt(h.start_date)} – {fmt(h.end_date)}
                {h.location && <> · <HiOutlineMapPin/> {h.location}</>}
              </p>
              {h.room_type_name && <p className="mrt__history-room">{h.room_type_name}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
